import { ref } from 'vue';

/**
 * Consentement préalable aux traceurs non essentiels (art. 82 loi Informatique
 * et Libertés, lignes directrices CNIL 2020). Rien n'est déposé ni chargé avant
 * un choix explicite ; refuser est aussi simple qu'accepter ; le choix est
 * redemandé après 6 mois et modifiable à tout moment (« Gérer les cookies »).
 *
 * Utilisé : `analytics` (Google Analytics, lib/analytics.ts). Tout autre outil
 * (chat, vidéo…) DOIT aussi passer par `whenConsented`, jamais par une balise
 * <script> directe.
 */
export type ConsentCategory = 'analytics' | 'marketing';
export type ConsentChoices = Record<ConsentCategory, boolean>;

interface StoredConsent {
  choices: ConsentChoices;
  /** Horodatage du choix (preuve du consentement). */
  at: string;
  version: number;
}

/** À incrémenter quand la liste des traceurs change : le choix est redemandé. */
const CONSENT_VERSION = 2; // 2 : ajout de Google Analytics
const STORAGE_KEY = 'billpulse.consent';
const MAX_AGE_MS = 182 * 24 * 3600 * 1000;

export const CATEGORY_LABELS: Record<ConsentCategory, { title: string; description: string }> = {
  analytics: { title: "Mesure d'audience", description: 'Google Analytics, sur les pages publiques du site seulement : pages vues et provenance des visites, pour améliorer le site.' },
  marketing: { title: 'Marketing', description: 'Mesure de nos campagnes et contenus de tiers.' },
};

const NONE: ConsentChoices = { analytics: false, marketing: false };

function read(): StoredConsent | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredConsent;
    if (stored.version !== CONSENT_VERSION) return null;
    if (Date.now() - new Date(stored.at).getTime() > MAX_AGE_MS) return null;
    return stored;
  } catch {
    return null;
  }
}

const initial = typeof window === 'undefined' ? null : read();

/** Choix en vigueur ; tout à `false` tant que l'utilisateur n'a rien accepté. */
export const consent = ref<ConsentChoices>(initial ? { ...NONE, ...initial.choices } : { ...NONE });
/** Vrai tant qu'aucun choix valide n'existe : le bandeau s'affiche. */
export const consentPending = ref(initial === null);
/** Ouvre le panneau de personnalisation (lien « Gérer les cookies »). */
export const consentPanelOpen = ref(false);

const listeners: Array<{ category: ConsentCategory; run: () => void; done: boolean }> = [];

export function saveConsent(choices: ConsentChoices): void {
  consent.value = { ...choices };
  consentPending.value = false;
  consentPanelOpen.value = false;
  try {
    const stored: StoredConsent = { choices, at: new Date().toISOString(), version: CONSENT_VERSION };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Stockage bloqué : le choix vaut pour cette visite seulement.
  }
  for (const l of listeners) {
    if (!l.done && choices[l.category]) {
      l.done = true;
      l.run();
    }
  }
  // Un retrait de consentement ne peut pas « décharger » un script déjà exécuté :
  // on recharge pour repartir sans lui.
  if (listeners.some((l) => l.done && !choices[l.category])) window.location.reload();
}

export const acceptAll = () => saveConsent({ analytics: true, marketing: true });
export const refuseAll = () => saveConsent({ ...NONE });

export function openConsentPanel(): void {
  consentPanelOpen.value = true;
}

/** Exécute `run` dès que (et seulement si) la catégorie est acceptée. */
export function whenConsented(category: ConsentCategory, run: () => void): void {
  const entry = { category, run, done: false };
  listeners.push(entry);
  if (consent.value[category]) {
    entry.done = true;
    run();
  }
}
